import React, { useMemo, useState } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Alert,
  Input,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import supplierAdvanceRefundService, {
  recoveryStatusTagColor,
  refundVoucherStatusColor,
  type SupplierAdvanceRefundVoucher,
} from "@/services/supplierAdvanceRefund.service";
import { useCurrency } from "@/contexts/CurrencyContext";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const LIST_PATH = "/finance/ap/supplier-advance-refunds";

const SupplierAdvanceRefundsPage: React.FC = () => {
  const { t } = useTranslation("finance");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const exposureId = searchParams.get("exposure_id");
  const sarId = searchParams.get("supplier_advance_request_id");
  const poId = searchParams.get("purchase_order_id");
  const { formatCurrency } = useCurrency();

  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([] as any);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [voucherNumberFilter, setVoucherNumberFilter] = useState<string>("");

  const filters = useMemo(
    () => ({
      from_date: dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
      to_date: dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
      status: statusFilter || undefined,
      voucher_number: voucherNumberFilter || undefined,
      supplier_advance_request_id: sarId || undefined,
      purchase_order_id: poId || undefined,
    }),
    [dateRange, statusFilter, voucherNumberFilter, sarId, poId],
  );

  const { data: list = [], isLoading } = useQuery({
    queryKey: ["supplier-advance-refunds", filters],
    queryFn: () => supplierAdvanceRefundService.findAll(filters),
  });

  const { data: exposure } = useQuery({
    queryKey: ["supplier-advance-recovery-exposure", exposureId],
    queryFn: () => supplierAdvanceRefundService.findExposureById(exposureId!),
    enabled: !!exposureId,
  });

  const newHref = exposureId
    ? `${LIST_PATH}/new?exposure_id=${exposureId}`
    : sarId
      ? `${LIST_PATH}/new?supplier_advance_request_id=${sarId}`
      : `${LIST_PATH}/new`;

  const columns: ColumnsType<SupplierAdvanceRefundVoucher> = [
    {
      title: t("voucherNo"),
      dataIndex: "voucher_number",
      key: "voucher_number",
      fixed: "left",
      width: 130,
      render: (val: string, record) => (
        <a onClick={() => navigate(`${LIST_PATH}/${record.id}`)}>
          {val ?? "—"}
        </a>
      ),
    },
    {
      title: t("date"),
      dataIndex: "refund_date",
      key: "refund_date",
      width: 110,
      render: (d: string) => (d ? dayjs(d).format("DD MMM YYYY") : "—"),
    },
    {
      title: t("supplier"),
      key: "supplier",
      width: 180,
      ellipsis: true,
      render: (_, r) => r.supplier?.supplier_name ?? "—",
    },
    {
      title: t("po"),
      key: "po",
      width: 110,
      render: (_, r) => r.purchaseOrder?.po_number ?? "—",
    },
    {
      title: t("sar"),
      key: "sar",
      width: 110,
      render: (_, r) => r.supplierAdvanceRequest?.advance_request_number ?? "—",
    },
    {
      title: t("refund"),
      dataIndex: "refund_amount",
      key: "refund_amount",
      width: 120,
      align: "right",
      render: (v: number, r) => formatCurrency(Number(v), r.advance_currency),
    },
    {
      title: t("bankReceived"),
      dataIndex: "bank_received_amount",
      key: "bank_received_amount",
      width: 120,
      align: "right",
      render: (v: number, r) => formatCurrency(Number(v), r.refund_currency),
    },
    {
      title: tc("status"),
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (s: string) => (
        <Tag color={refundVoucherStatusColor(s)}>{tc(s.toLowerCase())}</Tag>
      ),
    },
    {
      title: tc("actions"),
      key: "actions",
      fixed: "right",
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`${LIST_PATH}/${record.id}`)}
          >
            {tc("view")}
          </Button>
          {record.status === "Draft" && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`${LIST_PATH}/${record.id}/edit`)}
            >
              {tc("edit")}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t("supplierAdvanceRefunds")}
          </Title>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16, maxWidth: 720 }}
            message={t("advanceRecoveryRefundRegister")}
            description={t("advanceRecoveryRefundRegisterDesc")}
          />
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(newHref)}
          >
            {t("newRefundVoucher")}
          </Button>
        </Col>
      </Row>

      {exposure && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("recoveryPending", {
            po: exposure.po_number,
            sar: exposure.advance_request_number,
          })}
          description={
            <Space direction="vertical" size={4}>
              <span>
                {t("supplierBalanceLine", {
                  name:
                    exposure.supplier_name ??
                    exposure.supplier?.supplier_name ??
                    "—",
                  currency: exposure.currency,
                  amount: Number(exposure.remaining_refundable_balance).toFixed(
                    2,
                  ),
                })}
              </span>
              <Tag color={recoveryStatusTagColor(exposure.recovery_status)}>
                {exposure.recovery_status}
              </Tag>
              <Button
                type="primary"
                size="small"
                onClick={() => navigate(newHref)}
              >
                {t("createRefundVoucher")}
              </Button>
            </Space>
          }
        />
      )}

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange(v as any)}
          />
          <Select
            placeholder={tc("status")}
            allowClear
            style={{ width: 130 }}
            value={statusFilter || undefined}
            onChange={(v) => setStatusFilter(v ?? "")}
            options={[
              { value: "Draft", label: tc("draft") },
              { value: "Submitted", label: tc("submitted") },
              { value: "Approved", label: tc("approved") },
              { value: "Posted", label: tc("posted") },
              { value: "Cancelled", label: tc("cancelled") },
            ]}
          />
          <Input
            placeholder={t("voucherNumberPlaceholder")}
            allowClear
            style={{ width: 160 }}
            value={voucherNumberFilter}
            onChange={(e) => setVoucherNumberFilter(e.target.value)}
          />
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={isLoading}
          sticky={{ offsetHeader: 0 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t("totalVouchersCount", { count: total }),
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          scroll={{ x: 1200, y: 520 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default SupplierAdvanceRefundsPage;
